import { Controller } from "@hotwired/stimulus";

import { whenLayoutSettled } from "../utils/layout_settle";

export default class extends Controller {
  static targets = ["frame"];

  static values = {
    shellSrc: String,
    refreshIntervalSeconds: { type: Number, default: 0 },
    device: { type: String, default: "default" },
  };

  connect() {
    this.layoutSettleAborted = false;
    this.beforeCacheHandler = () => this.stripFrameSrc();
    document.addEventListener("turbo:before-cache", this.beforeCacheHandler);

    this.initializeShellSrc();
    this.startRefreshTimer();
  }

  disconnect() {
    this.layoutSettleAborted = true;
    document.removeEventListener("turbo:before-cache", this.beforeCacheHandler);
    this.stopRefreshTimer();
  }

  initializeShellSrc() {
    if (this.usesAutoDeviceLayout()) {
      whenLayoutSettled(() => {
        if (this.layoutSettleAborted) {
          return;
        }
        this.restoreShellSrc();
      });
      return;
    }

    this.restoreShellSrc();
  }

  usesAutoDeviceLayout() {
    return (this.deviceValue || "default").trim().toLowerCase() === "default";
  }

  restoreShellSrc() {
    if (!this.hasFrameTarget || !this.shellSrcValue) {
      return;
    }
    if (!this.frameTarget.getAttribute("src")) {
      this.frameTarget.src = this.shellSrcValue;
    }
  }

  cacheBustedShellSrc() {
    const separator = this.shellSrcValue.includes("?") ? "&" : "?";
    return `${this.shellSrcValue}${separator}_=${Date.now()}`;
  }

  reloadShell() {
    if (!this.hasFrameTarget || !this.shellSrcValue) {
      return;
    }
    this.frameTarget.src = this.cacheBustedShellSrc();
  }

  startRefreshTimer() {
    this.stopRefreshTimer();

    const intervalMs = this.refreshIntervalSecondsValue * 1000;
    if (!intervalMs) {
      return;
    }

    this.refreshTimer = window.setInterval(() => {
      this.reloadShell();
    }, intervalMs);

    this.hiddenAt = null;
    this.visibilityHandler = () => {
      if (document.visibilityState === "hidden") {
        this.hiddenAt = Date.now();
        return;
      }
      if (this.hiddenAt && Date.now() - this.hiddenAt >= intervalMs) {
        this.reloadShell();
      }
      this.hiddenAt = null;
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  stopRefreshTimer() {
    if (this.refreshTimer) {
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  stripFrameSrc() {
    if (this.hasFrameTarget) {
      this.frameTarget.removeAttribute("src");
    }
  }
}
